import { useCallback, useEffect, useState } from "react";
import { Drawer, Modal } from "antd";
import { ExternalLink, MapPin, MessageCircle, Phone, Plus, RefreshCw, Search, Telescope } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import {
    OUTBOUND_DISCOVER_URL, OUTBOUND_PROSPECTS_URL, OUTBOUND_PROSPECT_BULK_URL,
    OUTBOUND_SEQUENCES_URL, outboundProspectUrl, outboundSequenceEnrollUrl,
} from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { Btn, inputCls, OutboundHeader, Pill, PROSPECT_TONE, Section, useOpenLead, VERIFY_TONE } from "./ui";

/**
 * Outbound -> Prospects. Find organisations (OpenStreetMap, free) or paste a
 * list, see what their website says, and put the good ones in a sequence.
 */

const CATEGORY_LABELS = { hospital: "Hospitals", clinic: "Clinics", doctors: "Doctors", dentist: "Dentists", laboratory: "Labs" };

// Where a mobile number came from (prospecting/outreach.py set_mobile).
export const MOBILE_HOW = {
    map: "Map listing", website: "Website", whatsapp_link: "WhatsApp link on website",
    web_search: "Web search", manual: "Entered by staff",
};
const WA_STATUS = {
    pending: ["WhatsApp waiting", "blue"], sending: ["WhatsApp sending", "blue"], sent: ["WhatsApp sent", "green"],
    skipped: ["WhatsApp not sent", "gray"], failed: ["WhatsApp failed", "red"],
};
const SEARCH_LABEL = { queued: "Searching the web…", none: "No mobile found", failed: "Search failed" };

export const formatMobile = (m) => (m && m.length === 10 ? `${m.slice(0, 5)} ${m.slice(5)}` : m || "");

/** The Mobile column: the number, where it came from, and its WhatsApp. */
export function MobileCell({ r }) {
    if (!r.mobile) {
        return <span className={r.mobile_search === "queued" ? "text-sky-600" : "text-gray-400"} title={r.mobile_note || ""}>
            {SEARCH_LABEL[r.mobile_search] || "–"}</span>;
    }
    const [waLabel, waTone] = WA_STATUS[r.whatsapp_status] || [];
    return (
        <div className="space-y-0.5">
            <p className="flex items-center gap-1 font-medium text-gray-900 dark:text-white tabular-nums whitespace-nowrap">
                <Phone className="h-3 w-3 text-gray-400" aria-hidden="true" /> {formatMobile(r.mobile)}
            </p>
            <div className="flex flex-wrap gap-1">
                <span className="text-[10px] text-gray-500" title={r.mobile_source || ""}>{MOBILE_HOW[r.mobile_how] || r.mobile_how}</span>
                {!r.mobile_confirmed && <Pill tone="amber" title={r.mobile_note}>check number</Pill>}
                {waLabel && <Pill tone={waTone} title={r.whatsapp_reason || r.whatsapp_sent_at || ""}>{waLabel}</Pill>}
                {r.link_visits > 0 && <Pill tone="green" title={`${r.link_visits} visit(s)`}>Opened link</Pill>}
            </div>
        </div>
    );
}

function DiscoverPanel({ onStarted }) {
    const { fetch, create } = useApiMethods();
    const [area, setArea] = useState("");
    const [cats, setCats] = useState(["hospital", "clinic"]);
    const [runs, setRuns] = useState([]);
    const [busy, setBusy] = useState(false);
    const [paste, setPaste] = useState(false);
    const [lines, setLines] = useState("");

    const load = useCallback(() => {
        fetch(OUTBOUND_DISCOVER_URL).then(res => { if (res?.status === "success") setRuns(res.data); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);
    // While a search runs, look again every few seconds.
    useEffect(() => {
        if (!runs.some(r => r.status === "running")) return undefined;
        const t = setTimeout(() => { load(); onStarted(); }, 5000);
        return () => clearTimeout(t);
    }, [runs, load, onStarted]);

    const start = async () => {
        setBusy(true);
        try {
            const res = await create(OUTBOUND_DISCOVER_URL, { area, categories: cats });
            if (res?.status === "success") { ToastMsg("success", res.message); setArea(""); load(); }
            else ToastMsg("error", res?.message || "Could not start the search");
        } finally { setBusy(false); }
    };
    const addLines = async () => {
        const res = await create(OUTBOUND_PROSPECTS_URL, { lines });
        if (res?.status === "success") { ToastMsg("success", res.message); setLines(""); setPaste(false); onStarted(); }
        else ToastMsg("error", res?.message || "Could not add");
    };

    return (
        <Section title="Find prospects"
            subtitle="OpenStreetMap is free but thin: most clinics there have no website or email. Paste a list you already have to fill the gap."
            right={<Btn onClick={() => setPaste(true)}><Plus className="h-3.5 w-3.5" /> Paste a list</Btn>}>
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <MapPin className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
                    <input value={area} onChange={e => setArea(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && area.trim()) start(); }}
                        placeholder="City or district, e.g. Coimbatore, Tamil Nadu" className={cn(inputCls, "pl-7")} />
                </div>
                {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={cats.includes(k)}
                            onChange={() => setCats(c => c.includes(k) ? c.filter(x => x !== k) : [...c, k])} /> {label}
                    </label>
                ))}
                <Btn variant="primary" onClick={start} disabled={busy || area.trim().length < 3 || !cats.length}>
                    <Telescope className="h-3.5 w-3.5" /> {busy ? "Starting…" : "Search"}
                </Btn>
            </div>
            {runs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {runs.slice(0, 6).map(r => (
                        <span key={r.id} title={r.error || ""}
                            className="rounded-md border border-gray-200 dark:border-gray-700 px-2 py-1 text-[11px] text-gray-600 dark:text-gray-300">
                            <b>{r.area}</b>{" · "}
                            {r.status === "running" ? "searching…" : r.status === "failed" ? <span className="text-red-600">{r.error}</span>
                                : `${r.found} found, ${r.created} new${r.existing_leads ? `, ${r.existing_leads} already leads` : ""}`}
                        </span>
                    ))}
                </div>
            )}
            <Modal open={paste} onCancel={() => setPaste(false)} title="Paste prospects" okText="Add" onOk={addLines}
                okButtonProps={{ disabled: !lines.trim() }}>
                <p className="text-xs text-gray-500 mb-2">One per line: <code>Name, website, city, email</code>. Only the name is required. Organisations that are already leads are kept out of outbound automatically.</p>
                <textarea rows={10} value={lines} onChange={e => setLines(e.target.value)} className={inputCls}
                    placeholder={"Ganga Hospital, gangahospital.com, Coimbatore\nSri Ramakrishna Hospital, sriramakrishnahospital.com, Coimbatore, info@srh.in"} />
            </Modal>
        </Section>
    );
}

function ProspectDrawer({ id, onClose, onChanged }) {
    const { fetch, create } = useApiMethods();
    const [p, setP] = useState(null);
    const [website, setWebsite] = useState("");
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const openLead = useOpenLead();

    useEffect(() => {
        if (!id) return;
        setP(null);
        fetch(outboundProspectUrl(id)).then(res => {
            if (res?.status === "success") { setP(res.data); setWebsite(res.data.website || ""); }
        });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const act = async (body, ok) => {
        const res = await create(outboundProspectUrl(id), body);
        if (res?.status === "success") { setP(res.data); ToastMsg("success", ok); onChanged(); }
        else ToastMsg("error", res?.message || "Could not update");
    };

    return (
        <Drawer open={!!id} onClose={onClose} width={560} title={p?.name || "Prospect"} destroyOnClose>
            {!p ? <p className="text-xs text-gray-400">Loading…</p> : (
                <div className="space-y-4 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                        <Pill tone={PROSPECT_TONE[p.status]}>{p.status_label}</Pill>
                        <Pill tone="brand">Score {p.score}</Pill>
                        {p.category && <Pill>{p.category}</Pill>}
                        {p.customer_id && <button onClick={() => openLead(p.customer_id)} className="text-primary underline">Open lead #{p.customer_id}</button>}
                    </div>
                    <div className="text-gray-600 dark:text-gray-300 space-y-0.5">
                        {p.address && <p>{p.address}</p>}
                        <p>{[p.city, p.state].filter(Boolean).join(", ")}{p.phone && ` · ${p.phone}`}</p>
                        {p.source_ref?.startsWith("osm:") && (
                            <a className="text-primary inline-flex items-center gap-1" target="_blank" rel="noreferrer"
                                href={`https://www.openstreetmap.org/${p.source_ref.split(":")[1]}/${p.source_ref.split(":")[2]}`}>
                                OpenStreetMap entry <ExternalLink className="h-3 w-3" />
                            </a>
                        )}
                    </div>

                    <div className="space-y-1">
                        <p className="font-semibold text-gray-900 dark:text-white">Website</p>
                        <div className="flex gap-2">
                            <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="Paste their website if you know it" className={inputCls} />
                            <Btn onClick={() => act({ action: "set_website", website }, "Saved. Reading the site now.")} disabled={!website.trim()}>
                                <RefreshCw className="h-3.5 w-3.5" /> Read
                            </Btn>
                        </div>
                        {p.profile_error && <p className="text-amber-700 dark:text-amber-300">{p.profile_error}</p>}
                        {p.profiled_at && <p className="text-gray-400">Last read {p.profiled_at}</p>}
                    </div>

                    <div className="space-y-1">
                        <p className="font-semibold text-gray-900 dark:text-white">Mobile for WhatsApp</p>
                        <MobileCell r={p} />
                        {p.mobile_source && (p.mobile_source.startsWith("http")
                            ? <a href={p.mobile_source} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 break-all">Source <ExternalLink className="h-3 w-3" /></a>
                            : <p className="text-gray-400">{p.mobile_source}</p>)}
                        {p.mobile_note && <p className="text-gray-500">{p.mobile_note}</p>}
                        {p.whatsapp_reason && <p className="text-gray-500">WhatsApp: {p.whatsapp_reason}</p>}
                        <div className="flex gap-2 pt-1">
                            <input value={mobile} onChange={e => setMobile(e.target.value)} aria-label="Mobile number"
                                placeholder={p.mobile && !p.mobile_confirmed ? "Checked it? Type it to confirm" : "Type a 10-digit mobile"} className={inputCls} />
                            <Btn onClick={() => { act({ action: "set_mobile", mobile }, "Mobile saved"); setMobile(""); }}
                                disabled={mobile.replace(/\D/g, "").length < 10}>Save</Btn>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <p className="font-semibold text-gray-900 dark:text-white">What their site says</p>
                        {p.observations.length === 0 ? <p className="text-gray-400">Nothing recorded yet.</p> : (
                            <table className="w-full">
                                <tbody>
                                    {p.observations.map(o => (
                                        <tr key={o.signal} className="border-b border-gray-100 dark:border-gray-700/60 align-top">
                                            <td className="py-1 pr-2 text-gray-700 dark:text-gray-300">{o.label}</td>
                                            <td className="py-1 pr-2 font-medium text-gray-900 dark:text-white">{o.value}</td>
                                            <td className="py-1 pr-2 text-gray-400 tabular-nums">{o.confidence}%</td>
                                            <td className="py-1">{o.source_url && <a href={o.source_url} target="_blank" rel="noreferrer" className="text-primary"><ExternalLink className="h-3 w-3" /></a>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="space-y-1">
                        <p className="font-semibold text-gray-900 dark:text-white">Email addresses</p>
                        {p.contacts.map(c => (
                            <div key={c.id} className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-gray-900 dark:text-white">{c.email}</span>
                                <Pill tone={VERIFY_TONE[c.verification]} title={c.verification_note}>{c.verification}</Pill>
                                {c.suppressed && <Pill tone="red">suppressed</Pill>}
                                {c.is_primary ? <Pill tone="brand">will be mailed</Pill> : (
                                    <button className="text-primary underline" onClick={() => act({ action: "set_primary", contact_id: c.id }, "Primary address changed")}>use this one</button>
                                )}
                            </div>
                        ))}
                        <div className="flex gap-2 pt-1">
                            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Add an address they publish" className={inputCls} />
                            <Btn onClick={() => { act({ action: "add_contact", email }, "Address added"); setEmail(""); }} disabled={!email.includes("@")}>Add</Btn>
                        </div>
                    </div>

                    {p.enrollments.length > 0 && (
                        <div className="space-y-1">
                            <p className="font-semibold text-gray-900 dark:text-white">Sequences</p>
                            {p.enrollments.map(e => (
                                <p key={e.id} className="text-gray-700 dark:text-gray-300">{e.sequence} · {e.status_label} · {e.steps_sent} sent{e.stop_reason && ` · ${e.stop_reason}`}</p>
                            ))}
                        </div>
                    )}
                    {p.replies.length > 0 && (
                        <div className="space-y-2">
                            <p className="font-semibold text-gray-900 dark:text-white">Replies</p>
                            {p.replies.map(r => (
                                <div key={r.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                                    <p className="text-gray-500">{r.received_at} · {r.from_email} · <b>{r.intent || "unclassified"}</b></p>
                                    <p className="whitespace-pre-wrap text-gray-800 dark:text-gray-200 mt-1">{r.body.slice(0, 800)}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        {p.status === "excluded"
                            ? <Btn onClick={() => act({ action: "include" }, "Included again")}>Include again</Btn>
                            : <Btn variant="danger" onClick={() => act({ action: "exclude" }, "Excluded")} disabled={p.status === "converted"}>Exclude from outbound</Btn>}
                    </div>
                </div>
            )}
        </Drawer>
    );
}

export default function Prospects() {
    const { fetch, create } = useApiMethods();
    const [filters, setFilters] = useState({ q: "", status: "", city: "", signal: "", min_score: "", has_email: "", mobile: "" });
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [openId, setOpenId] = useState(null);
    const [sequences, setSequences] = useState([]);
    const [seqId, setSeqId] = useState("");

    const load = useCallback(() => {
        fetch(OUTBOUND_PROSPECTS_URL, { ...filters, page }).then(res => {
            if (res?.status === "success") setData(res);
            else ToastMsg("error", res?.message || "Could not load prospects");
        });
    }, [filters, page]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        fetch(OUTBOUND_SEQUENCES_URL).then(res => { if (res?.status === "success") setSequences(res.data.filter(s => s.status !== "completed")); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k, v) => { setPage(1); setSelected(new Set()); setFilters(f => ({ ...f, [k]: v })); };
    const rows = data?.data || [];
    const allOnPage = rows.length > 0 && rows.every(r => selected.has(r.id));
    const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const bulk = async (action) => {
        const res = await create(OUTBOUND_PROSPECT_BULK_URL, { action, ids: [...selected] });
        if (res?.status === "success") { ToastMsg("success", res.message); setSelected(new Set()); load(); }
        else ToastMsg("error", res?.message || "Could not do that");
    };
    const enrol = async () => {
        const res = await create(outboundSequenceEnrollUrl(seqId), { prospect_ids: [...selected] });
        if (res?.status === "success") {
            const why = Object.entries(res.skipped || {}).map(([k, v]) => `${v} ${k}`).join(", ");
            ToastMsg("success", res.message + (why ? ` Skipped: ${why}.` : ""));
            setSelected(new Set()); load();
        } else ToastMsg("error", res?.message || "Could not add");
    };
    const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

    return (
        <ContainerCard>
            <div className="p-4 space-y-4 overflow-auto">
                <OutboundHeader icon={Telescope} title="Prospects"
                    subtitle="Organisations that are not leads yet. Anyone already in the CRM is kept out of outbound automatically." />
                <DiscoverPanel onStarted={load} />

                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
                        <input value={filters.q} onChange={e => set("q", e.target.value)} placeholder="Name, domain, city" className={cn(inputCls, "pl-7 w-52")} />
                    </div>
                    <select value={filters.status} onChange={e => set("status", e.target.value)} className={cn(inputCls, "w-40")}>
                        <option value="">Any status</option>
                        {(data?.statuses || []).map(s => <option key={s.value} value={s.value}>{s.label} ({data.status_counts?.[s.value] || 0})</option>)}
                    </select>
                    <select value={filters.city} onChange={e => set("city", e.target.value)} className={cn(inputCls, "w-40")}>
                        <option value="">Any city</option>
                        {(data?.cities || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={filters.signal} onChange={e => set("signal", e.target.value)} className={cn(inputCls, "w-56")}>
                        <option value="">Any fact</option>
                        {(data?.signals || []).map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                    </select>
                    <input type="number" min={0} max={100} value={filters.min_score} onChange={e => set("min_score", e.target.value)} placeholder="Min score" className={cn(inputCls, "w-24")} />
                    <select value={filters.mobile} onChange={e => set("mobile", e.target.value)} aria-label="Mobile" className={cn(inputCls, "w-40")}>
                        <option value="">Any mobile</option>
                        <option value="found">Mobile found</option>
                        <option value="queued">Searching</option>
                        <option value="none">Not found</option>
                    </select>
                    <label className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={filters.has_email === "1"} onChange={e => set("has_email", e.target.checked ? "1" : "")} /> Verified email only
                    </label>
                </div>

                {selected.size > 0 && (
                    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-primary/5 border border-primary/20 px-3 py-2 text-xs">
                        <b>{selected.size} selected</b>
                        <select value={seqId} onChange={e => setSeqId(e.target.value)} className={cn(inputCls, "w-56")}>
                            <option value="">Add to sequence…</option>
                            {sequences.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <Btn variant="primary" disabled={!seqId} onClick={enrol}>Add</Btn>
                        <Btn onClick={() => bulk("profile")}><RefreshCw className="h-3.5 w-3.5" /> Read websites again</Btn>
                        <Btn onClick={() => bulk("find_mobile")}><Phone className="h-3.5 w-3.5" /> Find mobile numbers</Btn>
                        <Btn onClick={() => bulk("whatsapp")}><MessageCircle className="h-3.5 w-3.5" /> Send WhatsApp</Btn>
                        <Btn variant="danger" onClick={() => bulk("exclude")}>Exclude</Btn>
                        <Btn variant="ghost" onClick={() => setSelected(new Set())}>Clear</Btn>
                    </div>
                )}

                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr className="text-left text-gray-500 dark:text-gray-400">
                                <th className="p-2 w-8"><input type="checkbox" checked={allOnPage}
                                    onChange={() => setSelected(s => { const n = new Set(s); rows.forEach(r => allOnPage ? n.delete(r.id) : n.add(r.id)); return n; })} /></th>
                                <th className="p-2 font-medium">Organisation</th>
                                <th className="p-2 font-medium">City</th>
                                <th className="p-2 font-medium">Mobile</th>
                                <th className="p-2 font-medium">Email</th>
                                <th className="p-2 font-medium text-right">Score</th>
                                <th className="p-2 font-medium">Facts</th>
                                <th className="p-2 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!data && <tr><td colSpan={8} className="p-6 text-center text-gray-400">Loading…</td></tr>}
                            {data && rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-gray-400">No prospects yet. Search a city above.</td></tr>}
                            {rows.map(r => (
                                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                    <td className="p-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                                    <td className="p-2 cursor-pointer" onClick={() => setOpenId(r.id)}>
                                        <p className="font-medium text-gray-900 dark:text-white hover:text-primary">{r.name}</p>
                                        <p className="text-gray-400">{r.domain || r.website || "no website"}</p>
                                    </td>
                                    <td className="p-2 text-gray-700 dark:text-gray-300">{r.city}</td>
                                    <td className="p-2"><MobileCell r={r} /></td>
                                    <td className="p-2">
                                        {r.email ? <span className="flex items-center gap-1"><span className="text-gray-800 dark:text-gray-200">{r.email}</span><Pill tone={VERIFY_TONE[r.email_verification]}>{r.email_verification}</Pill></span> : <span className="text-gray-400">–</span>}
                                    </td>
                                    <td className="p-2 text-right tabular-nums font-semibold">{r.score}</td>
                                    <td className="p-2 text-gray-500">{r.signals.length}</td>
                                    <td className="p-2"><Pill tone={PROSPECT_TONE[r.status]}>{r.status_label}</Pill></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {pages > 1 && (
                    <div className="flex items-center justify-end gap-2 text-xs">
                        <Btn disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Btn>
                        <span>Page {page} of {pages} · {data.total} prospects</span>
                        <Btn disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</Btn>
                    </div>
                )}
            </div>
            <ProspectDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />
        </ContainerCard>
    );
}
